import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { Box, ContentLayoutF, H1, Separator } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';

import { AccountsCard } from './components/accounts-card/AccountsCard';
import { Balance } from './components/balance/Balance';
import { DividendsAndInterestCard } from './components/dividends-and-interest-card/DividendsAndInterestCard';
import { TaxSavingsCard } from './components/tax-savings-card/TaxSavingsCard';
import { PerformanceAccountsDocument } from './graphql/performanceAccounts.generated';

import { DateRangeOption } from '../../graphql/schema';
import { ApplicationLayout } from '../../Shared/layout/ApplicationLayout';
import { DateRangeFilter } from '../shared/date-range-filter/DateRangeFilter';

export const UserPerformancePage: React.FC = () => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DateRangeOption.AllTime);

  const { data, previousData } = useQuery(PerformanceAccountsDocument, {
    variables: { dateRangeOption },
  });

  const availableData = useMemo(() => data ?? previousData, [data, previousData]);

  return (
    <ErrorBoundary fallbackType="full" feature="performance_page" owner="investing_experience">
      <ApplicationLayout pageTitle="Performance" feature="performance_page">
        <ApplicationLayout.Header>
          <Box css={{ paddingY: 'space.300' }}>
            <H1 variant="title400">Performance</H1>
            <Separator css={{ fullWidthWithInset: 'space.0', marginTop: 'space.300', marginBottom: 'space.400' }} />
            <DateRangeFilter onChange={setDateRangeOption} selected={dateRangeOption} />
          </Box>
        </ApplicationLayout.Header>
        <ApplicationLayout.Content>
          <ContentLayoutF>
            <ContentLayoutF.SlotContainer>
              <ContentLayoutF.Slot1
                css={{ display: 'grid', gap: { base: 'space.100', sm: 'space.300' }, gridAutoRows: 'max-content' }}
              >
                {availableData && (
                  <>
                    <Balance dateRangeOption={dateRangeOption} accounts={availableData.performance.accounts} />
                    <AccountsCard dateRangeOption={dateRangeOption} accounts={availableData.performance.accounts} />
                  </>
                )}
              </ContentLayoutF.Slot1>
              <ContentLayoutF.Slot2
                css={{ display: 'grid', gap: { base: 'space.100', sm: 'space.300' }, gridAutoRows: 'max-content' }}
              >
                <DividendsAndInterestCard dateRangeOption={dateRangeOption} />
                <TaxSavingsCard dateRangeOption={dateRangeOption} />
              </ContentLayoutF.Slot2>
            </ContentLayoutF.SlotContainer>
          </ContentLayoutF>
        </ApplicationLayout.Content>
      </ApplicationLayout>
    </ErrorBoundary>
  );
};
